import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import { useCreateInstrument } from "../../api/hooks/useCreateInstrument";
import { useInstruments } from "../../api/hooks/useInstruments";
import type { components } from "../../api/schema";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { useToast } from "../ui/toast";

type InstrumentResponse = components["schemas"]["InstrumentResponse"];

const RECENT_KEY = "recentInstruments";
const RECENT_MAX = 5;
const SEARCH_DEBOUNCE_MS = 300;
const SYMBOL_PATTERN = /^[A-Za-z0-9./_-]+$/;

function readRecents(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === "string").slice(0, RECENT_MAX);
  } catch {
    return [];
  }
}

function writeRecents(ids: string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(ids.slice(0, RECENT_MAX)));
  } catch {
    // ignore
  }
}

function pushRecent(id: string): string[] {
  const next = [id, ...readRecents().filter((existing) => existing !== id)].slice(
    0,
    RECENT_MAX,
  );
  writeRecents(next);
  return next;
}

export type InstrumentTypeaheadProps = {
  value: string;
  onChange: (instrumentId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
};

type RowKind = "recent" | "result" | "add";

type Row =
  | { kind: "recent" | "result"; instrument: InstrumentResponse }
  | { kind: "add"; query: string };

export function InstrumentTypeahead({
  value,
  onChange,
  disabled,
  placeholder = "Type a symbol (e.g. AAPL)",
  id,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: InstrumentTypeaheadProps) {
  const listboxId = useId();
  const { data, isLoading } = useInstruments({ limit: 200, sort_key: "symbol" });
  const createInstrument = useCreateInstrument();
  const { showToast } = useToast();

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [pendingAddQuery, setPendingAddQuery] = useState<string | null>(null);
  const [recentIds, setRecentIds] = useState<string[]>(() => readRecents());
  const [localInstruments, setLocalInstruments] = useState<InstrumentResponse[]>(
    [],
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Merge in instruments created during this session so a freshly-created
  // symbol resolves immediately, before the invalidated list query refetches
  // (otherwise the field blanks out after a successful create).
  const instruments = useMemo(() => {
    const merged = new Map<string, InstrumentResponse>();
    for (const i of localInstruments) merged.set(i.instrument_id, i);
    for (const i of data?.items ?? []) merged.set(i.instrument_id, i);
    return Array.from(merged.values()).filter((i) => !i.is_deleted);
  }, [data, localInstruments]);
  const instrumentById = useMemo(() => {
    const map = new Map<string, InstrumentResponse>();
    for (const i of instruments) map.set(i.instrument_id, i);
    return map;
  }, [instruments]);

  // Show the resolved symbol when a value is selected and the input is unfocused.
  const selected = value === "" ? undefined : instrumentById.get(value);
  const displayValue = isOpen ? query : (selected?.symbol ?? query);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [query]);

  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (
        containerRef.current &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("mousedown", onClick);
    };
  }, []);

  const rows: Row[] = useMemo(() => {
    const q = debouncedQuery.toLowerCase();
    const recents = recentIds
      .map((id) => instrumentById.get(id))
      .filter((i): i is InstrumentResponse => i !== undefined)
      .filter((i) => q === "" || i.symbol.toLowerCase().includes(q));
    const recentIdSet = new Set(recents.map((i) => i.instrument_id));

    const results =
      q === ""
        ? instruments.filter((i) => !recentIdSet.has(i.instrument_id)).slice(0, 25)
        : instruments
            .filter(
              (i) =>
                !recentIdSet.has(i.instrument_id) &&
                (i.symbol.toLowerCase().includes(q) ||
                  (i.description ?? "").toLowerCase().includes(q)),
            )
            .slice(0, 25);

    const acc: Row[] = [];
    for (const i of recents) acc.push({ kind: "recent", instrument: i });
    for (const i of results) acc.push({ kind: "result", instrument: i });

    const trimmed = debouncedQuery.trim();
    const exactMatch =
      trimmed !== "" &&
      instruments.some((i) => i.symbol.toLowerCase() === trimmed.toLowerCase());
    if (trimmed !== "" && !exactMatch && SYMBOL_PATTERN.test(trimmed)) {
      acc.push({ kind: "add", query: trimmed });
    }
    return acc;
  }, [debouncedQuery, instruments, instrumentById, recentIds]);

  useEffect(() => {
    if (highlight >= rows.length) setHighlight(0);
  }, [rows, highlight]);

  function selectInstrument(instrument: InstrumentResponse) {
    onChange(instrument.instrument_id);
    setRecentIds(pushRecent(instrument.instrument_id));
    setQuery("");
    setIsOpen(false);
    inputRef.current?.blur();
  }

  function activateRow(row: Row) {
    if (row.kind === "add") {
      setPendingAddQuery(row.query);
      return;
    }
    selectInstrument(row.instrument);
  }

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!isOpen) setIsOpen(true);
      setHighlight((h) => (rows.length === 0 ? 0 : (h + 1) % rows.length));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) setIsOpen(true);
      setHighlight((h) => (rows.length === 0 ? 0 : (h - 1 + rows.length) % rows.length));
    } else if (event.key === "Enter") {
      if (isOpen && rows[highlight]) {
        event.preventDefault();
        activateRow(rows[highlight]);
      }
    } else if (event.key === "Escape") {
      if (isOpen) {
        event.preventDefault();
        setIsOpen(false);
      }
    }
  }

  async function confirmCreate() {
    if (pendingAddQuery === null) return;
    try {
      const created = await createInstrument.mutateAsync({
        symbol: pendingAddQuery,
        description: null,
      });
      setLocalInstruments((prev) => [
        created,
        ...prev.filter((i) => i.instrument_id !== created.instrument_id),
      ]);
      showToast(`✓ ${created.symbol} added`);
      setPendingAddQuery(null);
      selectInstrument(created);
    } catch {
      // Surface a generic toast; create dialog stays open so the user can retry/cancel.
      showToast("Failed to create instrument. Try again.");
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        ref={inputRef}
        id={id}
        role="combobox"
        type="text"
        autoComplete="off"
        spellCheck={false}
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        disabled={disabled || isLoading}
        placeholder={isLoading ? "Loading instruments…" : placeholder}
        value={displayValue}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
          setHighlight(0);
        }}
        onFocus={() => {
          setIsOpen(true);
        }}
        onKeyDown={onInputKeyDown}
      />
      {isOpen && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-md border border-outline bg-surface-container shadow-lg"
        >
          {rows.length === 0 && (
            <li className="px-3 py-2 text-sm text-on-surface-muted">No matches.</li>
          )}
          {rows.map((row, index) => {
            const isHighlighted = index === highlight;
            const rowKey = row.kind === "add" ? `add:${row.query}` : `${row.kind}:${row.instrument.instrument_id}`;
            const baseClasses =
              "flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-sm";
            const stateClasses = isHighlighted
              ? "bg-surface-container-high"
              : "hover:bg-surface-container-high";
            if (row.kind === "add") {
              return (
                <li
                  key={rowKey}
                  role="option"
                  aria-selected={isHighlighted}
                  className={`${baseClasses} ${stateClasses} border-t border-outline-variant`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    activateRow(row);
                  }}
                  onMouseEnter={() => {
                    setHighlight(index);
                  }}
                >
                  <span className="text-primary">+ Add &lsquo;{row.query}&rsquo;</span>
                  <span className="text-xs text-on-surface-muted">Create instrument</span>
                </li>
              );
            }
            const tag: RowKind = row.kind;
            return (
              <li
                key={rowKey}
                role="option"
                aria-selected={isHighlighted}
                className={`${baseClasses} ${stateClasses}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  activateRow(row);
                }}
                onMouseEnter={() => {
                  setHighlight(index);
                }}
              >
                <span className="flex items-center gap-2">
                  <span className="font-medium text-on-surface">{row.instrument.symbol}</span>
                  {row.instrument.description !== null && (
                    <span className="text-xs text-on-surface-muted">{row.instrument.description}</span>
                  )}
                </span>
                {tag === "recent" && (
                  <span className="text-[10px] uppercase tracking-wide text-on-surface-muted">
                    Recent
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Dialog
        open={pendingAddQuery !== null}
        onOpenChange={(open) => {
          if (!open && !createInstrument.isPending) setPendingAddQuery(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create instrument?</DialogTitle>
            <DialogDescription>
              No instrument matches &lsquo;{pendingAddQuery ?? ""}&rsquo;. Add it now and use it for this trade.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={createInstrument.isPending}
              onClick={() => {
                setPendingAddQuery(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={createInstrument.isPending}
              onClick={() => {
                void confirmCreate();
              }}
            >
              {createInstrument.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
